/**
 * Authentication Context — admin frontend
 *
 * - Login via /auth/admin/login (loginSuperAdmin no backend filtra role=super_admin)
 * - Logout via /auth/admin/logout (limpa apenas kaive_admin_*)
 * - Cookies httpOnly carregados automaticamente
 * - Gate adicional no client: rejeita usuários que não sejam super_admin
 *   (defesa em profundidade — backend já garante via SELECT)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import apiClient from '@/api/client';
import { AUTH_ENDPOINTS, ADMIN_USER_TYPE, SUPER_ADMIN_ROLE } from '@/constants/auth';
import type { User, LoginResponse, AuthContextValue, LoginResult, AuthState } from '@/types/auth';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

function isSuperAdmin(user: User | null): boolean {
  return user?.userType === 'admin_user' && user?.role === SUPER_ADMIN_ROLE;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isLoggingIn: false,
    isLoggingOut: false,
  });

  const isInitializing = useRef(false);
  const loginAbortController = useRef<AbortController | null>(null);

  const initAuth = useCallback(async () => {
    try {
      const response = await apiClient.get<User>(AUTH_ENDPOINTS.ME);
      const user = response.data;

      if (!isSuperAdmin(user)) {
        // Usuário autenticado mas não é super_admin — limpa estado
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        user,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
    }
  }, []);

  useEffect(() => {
    if (isInitializing.current) return;
    isInitializing.current = true;

    const timeout = setTimeout(() => {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
      isInitializing.current = false;
    }, 10000);

    initAuth().finally(() => {
      clearTimeout(timeout);
      isInitializing.current = false;
    });

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      if (state.isLoggingIn) {
        return { success: false, error: 'Login já em andamento' };
      }

      if (loginAbortController.current) {
        loginAbortController.current.abort();
      }

      loginAbortController.current = new AbortController();
      const signal = loginAbortController.current.signal;

      setState(prev => ({ ...prev, isLoggingIn: true }));

      try {
        const response = await apiClient.post<LoginResponse>(
          AUTH_ENDPOINTS.LOGIN,
          {
            email,
            password,
            userType: ADMIN_USER_TYPE,
          },
          { signal }
        );

        if (signal.aborted) {
          return { success: false, error: 'Login cancelado' };
        }

        const { user: userData } = response.data;

        if (!isSuperAdmin(userData)) {
          // Defesa em profundidade — backend já filtra, mas mantém o gate aqui.
          setState(prev => ({ ...prev, isLoggingIn: false }));
          return {
            success: false,
            error: 'Acesso restrito a super administradores',
          };
        }

        setState(prev => ({
          ...prev,
          user: userData,
          isAuthenticated: true,
          isLoggingIn: false,
        }));

        return { success: true };
      } catch (error: unknown) {
        if (axios.isCancel(error)) {
          return { success: false, error: 'Login cancelado' };
        }

        setState(prev => ({ ...prev, isLoggingIn: false }));

        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message;
          return {
            success: false,
            error: errorMessage || 'Erro ao fazer login. Verifique suas credenciais.',
          };
        }

        return {
          success: false,
          error: 'Erro inesperado ao fazer login',
        };
      } finally {
        loginAbortController.current = null;
      }
    },
    [state.isLoggingIn]
  );

  const logout = useCallback(async (): Promise<void> => {
    if (state.isLoggingOut) return;

    setState(prev => ({ ...prev, isLoggingOut: true }));

    try {
      await apiClient.post(AUTH_ENDPOINTS.LOGOUT);
    } catch {
      // Estado local é limpo independente do resultado
    } finally {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isLoggingIn: false,
        isLoggingOut: false,
      });
    }
  }, [state.isLoggingOut]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
